/**
 * Activity Monitor
 * Tracks user activity to determine if user is idle
 * Used for smart JWT token refresh strategy
 */

import JWT_CONFIG from '../config/jwtConfig';

class ActivityMonitor {
  constructor(config = {}) {
    this.idleTimeout = config.idleTimeout || JWT_CONFIG.IDLE_TIMEOUT;
    this.activityEvents = config.activityEvents || JWT_CONFIG.ACTIVITY_EVENTS;

    this.lastActivityTime = Date.now();
    this.isIdle = false;
    this.listeners = [];
    this.idleCheckInterval = null;
    this.eventHandlers = [];
  }

  /**
   * Start monitoring user activity
   */
  start() {
    if (this.idleCheckInterval) {
      return; // Already started
    }

    // Reset activity time
    this.lastActivityTime = Date.now();
    this.isIdle = false;

    // Add event listeners for all activity events
    this.activityEvents.forEach(eventName => {
      const handler = () => this.recordActivity();
      this.eventHandlers.push({ eventName, handler });
      window.addEventListener(eventName, handler, { passive: true });
    });

    // Check idle status based on config
    this.idleCheckInterval = setInterval(() => {
      this.checkIdleStatus();
    }, JWT_CONFIG.IDLE_CHECK_INTERVAL);

    console.log('[ActivityMonitor] Started monitoring user activity');
  }

  /**
   * Stop monitoring user activity
   */
  stop() {
    // Remove all event listeners
    this.eventHandlers.forEach(({ eventName, handler }) => {
      window.removeEventListener(eventName, handler);
    });
    this.eventHandlers = [];

    // Clear interval
    if (this.idleCheckInterval) {
      clearInterval(this.idleCheckInterval);
      this.idleCheckInterval = null;
    }

    console.log('[ActivityMonitor] Stopped monitoring user activity');
  }

  /**
   * Record user activity
   */
  recordActivity() {
    const wasIdle = this.isIdle;
    this.lastActivityTime = Date.now();
    this.isIdle = false;

    // If was idle and now active, notify listeners
    if (wasIdle) {
      console.log('[ActivityMonitor] User became active again');
      this.notifyListeners('active');
    }
  }

  /**
   * Check if user is idle
   */
  checkIdleStatus() {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    const wasIdle = this.isIdle;

    if (timeSinceLastActivity >= this.idleTimeout) {
      this.isIdle = true;

      // If just became idle, notify listeners
      if (!wasIdle) {
        console.log('[ActivityMonitor] User became idle');
        this.notifyListeners('idle');
      }
    }
  }

  /**
   * Get current idle status
   */
  getIsIdle() {
    // Force check before returning status
    this.checkIdleStatus();
    return this.isIdle;
  }

  /**
   * Get time since last activity in milliseconds
   */
  getTimeSinceLastActivity() {
    return Date.now() - this.lastActivityTime;
  }

  /**
   * Add listener for idle/active state changes
   * @param {Function} callback - Called with ('idle' | 'active')
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners of state change
   */
  notifyListeners(state) {
    this.listeners.forEach(callback => {
      try {
        callback(state);
      } catch (error) {
        console.error('[ActivityMonitor] Error in listener callback:', error);
      }
    });
  }

  /**
   * Reset activity timer (useful for manual resets)
   */
  reset() {
    this.lastActivityTime = Date.now();
    this.isIdle = false;
    console.log('[ActivityMonitor] Activity timer reset');
  }
}

// Create singleton instance with config
const activityMonitor = new ActivityMonitor({
  idleTimeout: JWT_CONFIG.IDLE_TIMEOUT,
  activityEvents: JWT_CONFIG.ACTIVITY_EVENTS,
});

export default activityMonitor;
